/**
 * AircraftRegistrationCombobox — input livre + sugestões das aeronaves da
 * escola via /api/di/aircraft/registrations.
 *
 * - Mostra um datalist nativo (<datalist>) com as matrículas sugeridas.
 * - Permite escrever uma matrícula que NÃO está na lista (aeronave de outra
 *   escola). Nesse caso o voo é guardado mas NÃO conta para o total de horas
 *   de nenhuma aeronave da escola (filtragem feita no backend em
 *   compute_aircraft_hours_minutes).
 * - Mostra abaixo um pequeno hint indicando se a matrícula corrente está
 *   indexada ("CS-XYZ · Tecnam P92") ou se é externa ("não está na escola
 *   — não conta para horas").
 *
 * Props: value, onChange, placeholder, disabled, "data-testid", className
 */
import { useEffect, useId, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Input } from "./input";


export function AircraftRegistrationCombobox({
  value, onChange, placeholder = "Matrícula", disabled, className = "",
  "data-testid": testId,
  ...rest
}) {
  const [list, setList] = useState([]);
  const id = useId();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/di/aircraft/registrations`, { withCredentials: true })
      .then((r) => { if (!cancelled) setList(r.data || []); })
      .catch(() => { /* silent — combobox still works as plain input */ });
    return () => { cancelled = true; };
  }, []);

  const normalized = (value || "").trim().toUpperCase();
  const matched = useMemo(
    () => list.find((a) => (a.registration || "").toUpperCase() === normalized),
    [list, normalized],
  );

  return (
    <div className={className}>
      <Input
        {...rest}
        list={id}
        value={value || ""}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
      />
      <datalist id={id}>
        {list.map((a) => (
          <option key={a.registration} value={a.registration}>
            {a.model || ""}
          </option>
        ))}
      </datalist>
      {normalized && (
        matched ? (
          <div className="mt-0.5 text-[10px] text-emerald-400" data-testid={`${testId}-status`}>
            ✓ {matched.registration} · {matched.model}{matched.status && matched.status !== "ativa" ? ` (${matched.status})` : ""}
          </div>
        ) : (
          <div className="mt-0.5 text-[10px] text-amber-400" data-testid={`${testId}-status`}>
            ⚠ Aeronave externa — não conta para horas da escola
          </div>
        )
      )}
    </div>
  );
}

export default AircraftRegistrationCombobox;
